<script setup>
import CursorClickIcon from '@iconify-vue/ph/cursor-click';
import EyeIcon from '@iconify-vue/ph/eye';
import PercentIcon from '@iconify-vue/ph/percent';
import RankingIcon from '@iconify-vue/ph/ranking';
import ExportButton from '@js/components/buttons/ExportButton.vue';
import CardGrid from '@js/components/cards/CardGrid.vue';
import MetricCard from '@js/components/cards/MetricCard.vue';
import DataTableLayout from '@js/components/tables/DataTableLayout.vue';
import HeaderCell from '@js/components/tables/cells/HeaderCell.vue';
import LinkCell from '@js/components/tables/cells/LinkCell.vue';
import NumberCell from '@js/components/tables/cells/NumberCell.vue';
import PillCell from '@js/components/tables/cells/PillCell.vue';
import TextCell from '@js/components/tables/cells/TextCell.vue';
import { useDataTable } from '@js/utils/dataTable';
import { useRoute } from '@js/utils/route';
import { createColumnHelper } from '@tanstack/vue-table';
import { h } from 'vue';

const { tenant, websiteVariants, periods, dimension } = defineProps({
    tenant: {
        type: Object,
        required: true,
    },
    websiteVariants: {
        type: Array,
        required: true,
    },
    periods: {
        type: Array,
        required: true,
    },
    dimension: {
        type: String,
        required: true,
        validator: (value) => ['query', 'page'].includes(value),
    },
});

const { route } = useRoute();

const columnHelper = createColumnHelper();

const isPage = dimension === 'page';

const routeNames = isPage
    ? {
          url: 'front.projectManagement.seo.postGoogleSearchConsolePages',
          export: 'front.projectManagement.seo.postExportGoogleSearchConsolePages',
      }
    : {
          url: 'front.projectManagement.seo.postGoogleSearchConsoleKeywords',
          export: 'front.projectManagement.seo.postExportGoogleSearchConsoleKeywords',
      };

const valueColumn = columnHelper.accessor(() => null, {
    id: isPage ? 'url' : 'keyword',
    header: () => (isPage ? 'URL' : 'Mot-clé'),
    cell: (props) => {
        const value = isPage
            ? props.row.original.url
            : props.row.original.keyword;

        if (!value) {
            return null;
        }

        return isPage
            ? h(LinkCell, { text: value, href: value, maxWidth: 400 })
            : h(TextCell, { text: value });
    },
});

const periodColumns = periods.map((period) =>
    columnHelper.accessor(() => null, {
        id: `position_${period.value}`,
        headerCustom: true,
        header: (props) =>
            h(HeaderCell, {
                header: props.header,
                title: 'Position ' + period.label,
                align: 'right',
            }),
        cell: (props) => {
            const bucket = props.row.original.positions[period.value];

            if (!bucket || bucket.position === null) {
                return null;
            }

            return h(NumberCell, {
                number: bucket.position,
                align: 'right',
                withDecimals: true,
                variation: bucket.variation ?? undefined,
                variationWithDecimals: true,
                variationWithPlus: true,
            });
        },
    }),
);

const {
    loading,
    table,
    count,
    globalData,
    globalFilter,
    setGlobalFilter,
    availableFilters,
    filters,
    setFilters,
    selectionIds,
    exportData,
} = useDataTable({
    columns: [
        valueColumn,
        columnHelper.accessor(() => null, {
            id: 'website_variant_name',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Site',
                }),
            cell: (props) =>
                props.row.original.websiteVariantName
                    ? h(PillCell, {
                          pillValue: props.row.original.websiteVariantName,
                      })
                    : null,
        }),
        ...periodColumns,
        columnHelper.accessor(() => null, {
            id: 'impressions',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Impressions 28J',
                    align: 'right',
                }),
            cell: (props) =>
                props.row.original.impressions === null
                    ? null
                    : h(NumberCell, {
                          number: props.row.original.impressions,
                          align: 'right',
                      }),
        }),
        columnHelper.accessor(() => null, {
            id: 'clicks',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Clics 28J',
                    align: 'right',
                }),
            cell: (props) =>
                props.row.original.clicks === null
                    ? null
                    : h(NumberCell, {
                          number: props.row.original.clicks,
                          align: 'right',
                      }),
        }),
        columnHelper.accessor(() => null, {
            id: 'data_updated_at',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Dernière MÀJ',
                }),
            cell: (props) =>
                props.row.original.dataUpdatedAt
                    ? h(TextCell, { text: props.row.original.dataUpdatedAt })
                    : null,
        }),
    ],
    url: route(routeNames.url, { tenant: tenant.id }),
    exportUrl: route(routeNames.export, { tenant: tenant.id }),
    columnVisibility: {
        website_variant_name: websiteVariants.length > 1,
    },
    selectionIdKey: 'id',
});
</script>

<template>
    <DataTableLayout
        :table="table"
        :count="count"
        :global-filter="globalFilter"
        :set-global-filter="setGlobalFilter"
        :available-filters="availableFilters"
        :filters="filters"
        :set-filters="setFilters"
        :loading="loading"
    >
        <template #before-table>
            <CardGrid :columns="4">
                <MetricCard
                    title="Position moyenne 28J"
                    subtitle="Pondérée par les impressions"
                    :value="globalData.averagePosition"
                    :icon="RankingIcon"
                />

                <MetricCard
                    title="Impressions 28J"
                    :value="globalData.totalImpressions"
                    :icon="EyeIcon"
                    :with-decimals="false"
                />

                <MetricCard
                    title="Clics 28J"
                    :value="globalData.totalClicks"
                    :icon="CursorClickIcon"
                    :with-decimals="false"
                />

                <MetricCard
                    title="CTR moyen 28J"
                    :value="globalData.averageCtr"
                    type="percentage"
                    :icon="PercentIcon"
                />
            </CardGrid>
        </template>

        <template #actions>
            <ExportButton
                :csv-export-callback="() => exportData('csv')"
                :xlsx-export-callback="() => exportData('xlsx')"
                :disabled="selectionIds.length === 0"
            />
        </template>
    </DataTableLayout>
</template>
